import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

export interface DatasourceOption {
  id: string;
  label: string;
  /** GET /datasources/'s real category field (confirmed against this same endpoint's other
   * consumers — DataSourcesPanel.tsx, dataSourceUtils.tsx — which found it never sends a `type`
   * field, only this one): 'database' | 'cloud' | 'app' | 'website' | 'local' | other/unknown. */
  storageType?: string;
  /** Whether this datasource can actually be queried yet. Derived from the same
   * embedding_status enum DataSourcesPanel.tsx/AdminHub.tsx already use (1=in progress,
   * 2=completed, 3=failed, 4=scraping a website) — ready only once embedding has completed, or
   * immediately if this datasource never required embedding in the first place. */
  ready: boolean;
  /** Only set when !ready, to label *why* (e.g. "Processing…", "Failed") rather than a single
   * generic "not ready" badge for every case. */
  notReadyReason?: string;
}

export interface DatasourceLookupPage {
  items: DatasourceOption[];
  hasMore: boolean;
}

// PR #680 re-review (Medium) — the "both fields absent means ready" fallback below was a blanket
// assumption, unconfirmed against real payloads, that could misfire on a website/file datasource
// not yet processed. Narrowed to only the storage_types that genuinely never go through embedding
// at all (connections, not content) — database/cloud/app. 'website' and 'local' (file) datasources
// are always embedded (see DataSourcesPanel.tsx's crawl/scrape and upload flows), so missing
// fields for those stay fail-closed instead of being read as "doesn't apply here".
const NEVER_EMBEDDED_STORAGE_TYPES = new Set(['database', 'cloud', 'app']);

/**
 * Readiness for the picker, from the same embedding_status enum AdminHub.tsx's
 * getProcessingStatusChip already renders (1=In Progress, 2=Completed, 3=Failed; 4=Scraping is
 * DataSourcesPanel.tsx's website-specific in-progress value). A datasource that never required
 * embedding at all (is_embedding_required === false) is queryable immediately regardless of
 * embedding_status. Any other/missing status (0, undefined, a future value) is treated as "not
 * ready yet" rather than guessed as ready — never silently allow picking something unproven.
 *
 * PR #680 review (Medium) — that fail-closed default was too strict for a connection-type
 * datasource (e.g. a database/app/cloud connection) that never goes through embedding at all:
 * the list endpoint may omit `is_embedding_required` entirely for those instead of sending
 * `false`, which previously fell into the same "Not ready yet" bucket as an unproven item. Only
 * when BOTH fields are absent — never explicitly false, and no status ever recorded — AND
 * `storageType` is one of the never-embedded connection types above, is this read as "embedding
 * doesn't apply here" and treated as ready; the moment either field is present, or the storage
 * type is one that's actually embedded, the real status still governs.
 */
export function resolveReadiness(
  isEmbeddingRequired: boolean | undefined,
  embeddingStatus: number | undefined,
  storageType: string | undefined
): { ready: boolean; notReadyReason?: string } {
  if (isEmbeddingRequired === false) return { ready: true };
  if (
    isEmbeddingRequired === undefined &&
    embeddingStatus === undefined &&
    storageType !== undefined &&
    NEVER_EMBEDDED_STORAGE_TYPES.has(storageType)
  ) {
    return { ready: true };
  }
  switch (embeddingStatus) {
    case 2:
      return { ready: true };
    case 1:
      return { ready: false, notReadyReason: 'Processing…' };
    case 4:
      return { ready: false, notReadyReason: 'Scraping…' };
    case 3:
      return { ready: false, notReadyReason: 'Failed to process' };
    default:
      return { ready: false, notReadyReason: 'Not ready yet' };
  }
}

/**
 * dynamic_multiselect_FE_datasource_picker_spec.md §0/§3.2/§3.3/§5 — the spec's own endpoint
 * shape (`GET /datasources?channel=<id>&search=<term>&cursor=<cursor>`) is a placeholder pending
 * a BS spike. Research into this repo found that spike already answered: `/datasources/` is a
 * live, channel-scoped, paginated, searchable endpoint already used by DataSourcesPanel.tsx and
 * AdminHub.tsx — just with `channel_id`/`page`/`size` instead of the spec's `channel`/`cursor`
 * names. This talks to that real, already-live contract rather than guessing a new one; `page` is
 * 1-based, matching that existing usage's own convention.
 */
export async function listDatasourcesForChannel(
  channelId: string,
  options: { search?: string; page?: number; size?: number; signal?: AbortSignal } = {}
): Promise<DatasourceLookupPage> {
  const { search, page = 1, size = 20, signal } = options;
  const params = new URLSearchParams({
    channel_id: channelId,
    page: String(page),
    size: String(size),
  });
  if (search && search.trim()) params.append('search', search.trim());

  const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
  const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to load datasources (status ${response.status})`);
  }

  const data = await response.json();
  const rawItems: Array<{
    id: string;
    name: string;
    storage_type?: string;
    embedding_status?: number;
    is_embedding_required?: boolean;
  }> = data.datasources ?? [];
  const items: DatasourceOption[] = rawItems.map((d) => {
    const { ready, notReadyReason } = resolveReadiness(d.is_embedding_required, d.embedding_status, d.storage_type);
    return { id: d.id, label: d.name, storageType: d.storage_type, ready, notReadyReason };
  });
  const totalPages: number | undefined =
    data.pages ?? (data.total && data.size ? Math.ceil(data.total / data.size) : undefined);
  const hasMore = totalPages !== undefined ? page < totalPages : items.length === size;
  return { items, hasMore };
}
